import React, { useState } from 'react';
import { Mafs, Coordinates, Plot, Theme, Text, Point } from 'mafs';
import { Card, InputNumber, Statistic, Row, Col } from 'antd';

export const FinancialCalculatorWidget = ({ config = {}, variables = {} }) => {
    const [principal, setPrincipal] = useState(variables.principal || config.principal || 1000);
    const [rate, setRate] = useState(variables.rate || config.rate || 5);
    const [years, setYears] = useState(variables.years || config.years || 10);

    const r = (rate || 0) / 100;
    const p = principal || 0;
    const tVal = years || 0;

    const simple = (t) => p * (1 + r * t);
    const compound = (t) => p * Math.pow(1 + r, t);

    const finalSimple = simple(tVal);
    const finalCompound = compound(tVal);
    const difference = finalCompound - finalSimple;

    const maxY = Math.max(finalCompound, finalSimple) * 1.15;
    const maxX = tVal * 1.15;

    return (
        <div className="flexColumn gap-medium width-100">
            <div className="ch-emph-block" style={{ height: 380, overflow: 'hidden' }}>
                <Mafs
                    viewBox={{ x: [0, maxX], y: [0, maxY] }}
                    preserveAspectRatio={false}
                    pan={false}
                >
                    <Coordinates.Cartesian
                        xAxis={{ lines: true, labels: (n) => n % 2 === 0 ? n : '' }}
                        yAxis={{ lines: true, labels: (n) => n > 0 && n % 2000 === 0 ? `$${n/1000}k` : '' }}
                    />

                    <Plot.OfX y={simple} color={Theme.blue} label="Simple" />
                    <Plot.OfX y={compound} color={Theme.red} label="Compound" />

                    <Point x={tVal} y={finalSimple} color={Theme.blue} />
                    <Text x={tVal} y={finalSimple} attach="nw" color={Theme.blue} size={14}>
                        Simple: ${finalSimple.toFixed(0)}
                    </Text>

                    <Point x={tVal} y={finalCompound} color={Theme.red} />
                    <Text x={tVal} y={finalCompound} attach="sw" color={Theme.red} size={14}>
                        Compound: ${finalCompound.toFixed(0)}
                    </Text>
                </Mafs>
            </div>

            <Card className="ch-card shadow-sm border-all--s">
                <Row gutter={24} align="middle">
                    <Col xs={24} md={12}>
                        <div className="flexColumn gap-small">
                            <div className="flexSpaceBetween flexAlignCentre">
                                <span className="ch-text-bold">Principal ($)</span>
                                <InputNumber
                                    min={100} max={100000} step={100}
                                    value={principal} onChange={v => setPrincipal(v ?? 0)}
                                    style={{ width: 120 }}
                                />
                            </div>
                            <div className="flexSpaceBetween flexAlignCentre">
                                <span className="ch-text-bold">Rate (%)</span>
                                <InputNumber
                                    min={0.1} max={20} step={0.1}
                                    value={rate} onChange={v => setRate(v ?? 0)}
                                    style={{ width: 120 }}
                                />
                            </div>
                            <div className="flexSpaceBetween flexAlignCentre">
                                <span className="ch-text-bold">Time (Years)</span>
                                <InputNumber
                                    min={1} max={50} step={1}
                                    value={years} onChange={v => setYears(v ?? 0)}
                                    style={{ width: 120 }}
                                />
                            </div>
                        </div>
                    </Col>

                    <Col xs={24} md={12} className="border-l--s padding-l--l">
                        <Statistic
                            title="Extra Earned with Compound"
                            value={difference}
                            precision={2}
                            prefix="$"
                            valueStyle={{ color: difference > 0 ? '#3f8600' : '#cf1322', fontWeight: 'bold' }}
                        />
                    </Col>
                </Row>
            </Card>
        </div>
    );
};